import React from 'react';
import { useNavigate } from 'react-router';
import { useExperimentsStore } from 'apps/experiments/src/store';
import { Experiment } from 'apps/experiments/src/types';
import { useTranslation } from 'common/i18n/hooks';
import { computeStatusPercent } from 'apps/experiments/src/components/utils';
import { TableGroup } from 'common/components/table-react';
import { createTableHelper } from 'common/components/table-react/helpers';
import { Row } from 'common/components/table-react/types';
import { getReportUrl } from 'common/report/util';
import { useExperimentPageContext } from 'apps/experiments/src/components/context';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { ReportBannersReact } from 'common/widgets/report-banners';
import { isTestEnv } from 'common/report/util/env';
import { useDuplicateExperiment } from 'iron/apps/experiments/src/components/hooks/duplicate-experiment';
import { useDeleteExperiment } from 'iron/apps/experiments/src/components/hooks/delete-experiment';
import styles from './index.module.styl';
import { ExperimentTableCellType } from './types';
import { customCellTypesCallback } from './custom-cell-types';
const tableHelper = createTableHelper<Experiment>();
const DELETE_ACTION = "delete";
const DUPLICATE_ACTION = "duplicate";
export function ExperimentListPageMain() {
  const i18n = useTranslation();
  const navigate = useNavigate();
  const {
    projectId,
    workspaceId
  } = useExperimentPageContext();
  const experiments = useExperimentsStore(state => state.experiments);
  const duplicateExperiment = useDuplicateExperiment({
    isListPage: true
  });
  const deleteExperiment = useDeleteExperiment({
    isListPage: true
  });
  const columns = React.useMemo(() => [tableHelper.accessorColumn("name", {
    cellType: ExperimentTableCellType.Text,
    headerTitle: i18n.t("experiments.list.column-name"),
    fractionSize: 200,
    enableColumnFilter: false,
    enableSorting: true
  }), tableHelper.accessorColumn("status", {
    cellType: ExperimentTableCellType.Status,
    headerTitle: i18n.t("experiments.list.column-status"),
    fractionSize: 140,
    enableColumnFilter: false,
    enableSorting: true,
    templateParams: {
      getStatus: (row: Row<Experiment>) => row.original.status,
      getPercent: (row: Row<Experiment>) => computeStatusPercent(row.original)
    }
  }), tableHelper.accessorColumn("results_cache", {
    cellType: ExperimentTableCellType.Results,
    headerTitle: i18n.t("experiments.list.column-results"),
    fractionSize: 140,
    enableColumnFilter: false,
    enableSorting: false
  }), tableHelper.accessorColumn("creator_name", {
    cellType: ExperimentTableCellType.UserInfo,
    headerTitle: i18n.t("experiments.list.column-creator"),
    fractionSize: 60,
    enableSorting: true,
    enableColumnFilter: false,
    templateParams: {
      getUserInfo: (row: Row<Experiment>) => ({
        id: row.original.creator_id,
        name: row.original.creator_name,
        email: row.original.creator_email
      })
    }
  }), tableHelper.accessorColumn("start_date", {
    cellType: ExperimentTableCellType.FormattedDate,
    headerTitle: i18n.t("experiments.list.column-start-date"),
    fractionSize: 100,
    enableSorting: true,
    enableColumnFilter: false
  }), tableHelper.accessorColumn("end_date", {
    cellType: ExperimentTableCellType.FormattedDate,
    headerTitle: i18n.t("experiments.list.column-end-date"),
    fractionSize: 100,
    enableSorting: true,
    enableColumnFilter: false
  }), tableHelper.displayColumn({
    id: "actions",
    headerTitle: "",
    fixedSize: 60,
    cell: context => {
      const experiment = context.row.original;
      const items = [{
        name: DUPLICATE_ACTION,
        label: i18n.t("common.duplicate"),
        icon: "duplicate"
      }];
      if (experiment.can_update_basic || experiment.allow_staff_override && experiment.is_superadmin) {
        items.push({
          name: DELETE_ACTION,
          label: i18n.t("common.delete"),
          icon: "trashcan"
        });
      }
      return <SelectReact backButton menuWidth={200} icon={{
        leftIcon: "hor-ellipsis"
      }} iconOnly theme={SelectTheme.Tertiary} tabIndex={0} sections={[{
        items
      }]} onChange={(ev: TODO) => {
        switch (ev.detail.item.name) {
          case DELETE_ACTION:
            deleteExperiment.mutate({
              id: experiment.id
            });
            break;
          case DUPLICATE_ACTION:
            duplicateExperiment.mutate({
              id: experiment.id
            });
            break;
        }
      }} onClick={ev => {
        ev.stopPropagation();
      }} />;
    }
  })], [i18n]);
  const customCellTypes = customCellTypesCallback(tableHelper, i18n);
  const handleRowClick = (row: Row<Experiment>) => {
    navigate(getReportUrl("experiments/".concat(row.original.id), {
      projectId,
      workspaceId
    }));
  };
  return <main className={styles.main} data-sentry-component="ExperimentListPageMain" data-sentry-source-file="index.tsx">
      {!isTestEnv() && <ReportBannersReact />}
      <TableGroup columns={columns} data={experiments !== null && experiments !== void 0 ? experiments : []} customCellTypes={customCellTypes} initializedColumnFilterState={[]} defaultColumnFilterState={[]} defaultSorting={[{
      id: "status",
      desc: false
    }]} data-sentry-element="TableGroup" data-sentry-source-file="index.tsx">
        <TableGroup.FilterSelectionBar filterSearchPlaceholder={i18n.t("experiments.list.search-placeholder")} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
        <TableGroup.Table onRowClick={handleRowClick} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
      </TableGroup>
    </main>;
}